import { safeFetchAPI } from '@/api';
import { log, storage, utils } from '@/helpers';
import { create } from 'zustand';

type UsernamesState = {
	loaded: boolean;
	usernames: Record<string, string>;
};

type UsernamesActions = {
	getUsername: (uid?: string, fetchIfMissing?: boolean) => string | undefined;
	fetchUsername: (uid?: string, skipCache?: boolean) => Promise<void>;
};

type UsernamesStore = UsernamesState & UsernamesActions;

const initialState: UsernamesState = {
	loaded: false,
	usernames: {},
};

export const useUsernamesStore = create<UsernamesStore>((set, get) => ({
	...initialState,

	getUsername: (uid?: string, fetchIfMissing = true) => {
		if (!uid) return undefined;
		log('getUsername', uid);
		if (utils.uid === uid) {
			// return utils.user.profile?.name;
		}

		const { usernames } = get();
		if (usernames[uid]) return usernames[uid];
		const cache = getUsernameCacheFromCache(uid);
		if (cache) return cache;
		if (fetchIfMissing) get().fetchUsername(uid);
		return usernames[uid];
	},

	async fetchUsername(uid?: string, skipCache = false) {
		if (!uid) return;

		if (!skipCache) {
			const cache = getUsernameCacheFromCache(uid);
			if (cache) {
				set({ usernames: { ...get().usernames, [uid]: cache }, loaded: true });
				return;
			}
		}

		const [data, error] = await safeFetchAPI<{ name: string }>(`/username/${uid}?bypass=true`);

		if (error) return;

		if (data) setUsernameCache(uid, data.name);

		set({ usernames: { ...get().usernames, [uid]: data?.name ?? '' }, loaded: true });
	},
}));

// Cache each username by UID for 15 minutes
// Craete a getUsernameCache function that returns the cached username if it exists and is not expired
// Create a setUsernameCache function that sets the cached username

const USERNAME_CACHE_KEY = 'usernames';
const USERNAME_CACHE_TTL = 1000 * 60 * 15; // 15 minutes

const getCacheKey = (uid?: string) => {
	if (!uid) return USERNAME_CACHE_KEY;
	return `${USERNAME_CACHE_KEY}_${uid}`;
};

const getUsernameCacheFromCache = (uid?: string): string | undefined => {
	if (!uid) return undefined;
	const cacheKey = getCacheKey(uid);
	const cache = storage.getRaw<string>(cacheKey);
	if (!cache) return undefined;
	const { value, expiration } = cache ?? {};
	if (expiration && expiration < Date.now()) {
		storage.remove(cacheKey);
		return undefined;
	}
	return value;
};

const setUsernameCache = (uid: string, username: string) => {
	storage.set(getCacheKey(uid), username, USERNAME_CACHE_TTL);
};

const clearUsernameCache = (uid?: string) => {
	if (!uid) return;
	storage.remove(getCacheKey(uid));
};
// @ts-ignore
window.clearUsernameCache = clearUsernameCache;
